import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ksefPasswordValidator } from 'shared/validators/ksef-password.validator';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

export interface KsefCertificateDialogData {
  hasCertificate: boolean;
  nip?: string;
}

export interface KsefCertificateDialogResult {
  certificatePem: string;
  privateKeyPem: string;
  privateKeyPassphrase: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule
  ],
  selector: 'app-ksef-certificate-dialog',
  templateUrl: './ksef-certificate-dialog.component.html',
  styleUrls: ['./ksef-certificate-dialog.component.scss']
})
export class KsefCertificateDialogComponent {
  public certificateFileName = '';
  public privateKeyFileName = '';
  public certificateFileError = '';
  public privateKeyFileError = '';
  public showPassphrase = false;

  get passwordValue(): string {
    return this.form?.controls?.privateKeyPassphrase?.value || '';
  }

  get hasMinLength(): boolean {
    return this.passwordValue.length >= 15;
  }

  get hasUpperCase(): boolean {
    return /[A-Z]/.test(this.passwordValue);
  }

  get hasLowerCase(): boolean {
    return /[a-z]/.test(this.passwordValue);
  }

  get hasNumber(): boolean {
    return /[0-9]/.test(this.passwordValue);
  }

  get hasSpecialChar(): boolean {
    return /[!@#$%^&*()\-_=+]/.test(this.passwordValue);
  }

  public form = new FormGroup({
    certificatePem: new FormControl('', [Validators.required, Validators.minLength(64)]),
    privateKeyPem: new FormControl('', [Validators.required, Validators.minLength(64)]),
    privateKeyPassphrase: new FormControl('', [Validators.required, ksefPasswordValidator()])
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KsefCertificateDialogData,
    private dialogRef: MatDialogRef<KsefCertificateDialogComponent, KsefCertificateDialogResult>
  ) {}

  public cancel(): void {
    this.dialogRef.close();
  }

  public async onCertificateFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.crt')) {
      this.certificateFileError = 'Wybierz plik certyfikatu z rozszerzeniem .crt';
      this.certificateFileName = '';
      this.form.controls.certificatePem.setValue('');
      input.value = '';
      return;
    }

    const content = await this.readFileAsText(file);
    this.certificateFileError = '';
    this.certificateFileName = file.name;
    this.form.controls.certificatePem.setValue(content.trim());
    this.form.controls.certificatePem.markAsTouched();
  }

  public async onPrivateKeyFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.key')) {
      this.privateKeyFileError = 'Wybierz plik klucza prywatnego z rozszerzeniem .key';
      this.privateKeyFileName = '';
      this.form.controls.privateKeyPem.setValue('');
      input.value = '';
      return;
    }

    const content = await this.readFileAsText(file);
    this.privateKeyFileError = '';
    this.privateKeyFileName = file.name;
    this.form.controls.privateKeyPem.setValue(content.trim());
    this.form.controls.privateKeyPem.markAsTouched();
  }

  public save(): void {
    if (this.form.invalid || !this.certificateFileName || !this.privateKeyFileName) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      certificatePem: this.form.controls.certificatePem.value || '',
      privateKeyPem: this.form.controls.privateKeyPem.value || '',
      privateKeyPassphrase: this.form.controls.privateKeyPassphrase.value || ''
    });
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Nie udało się odczytać pliku.'));
      reader.readAsText(file);
    });
  }
}
